import React from 'react';
import { X } from 'lucide-react';
import { cn } from '@/utils/cn';
import type { BadgeProps } from './Badge.types';

/**
 * # Badge
 *
 * Maly statusovy indikator nebo stitek.
 *
 * @example
 * <Badge variant="success">Aktivni</Badge>
 * <Badge variant="warning" dot>Pozor</Badge>
 * <Badge onRemove={handleRemove}>Tag 1</Badge>
 */
export const Badge: React.FC<BadgeProps> = ({
  variant = 'secondary',
  size = 'md',
  dot = false,
  icon,
  onRemove,
  children,
  className,
  'data-testid': testId,
  ...props
}) => {
  return (
    <span
      className={cn(
        'inline-flex items-center font-medium rounded-full border transition-colors',
        sizeClasses[size],
        variantClasses[variant],
        className,
      )}
      data-testid={testId}
      {...props}
    >
      {dot && (
        <span
          className={cn('rounded-full mr-1.5', dotSizeClasses[size], dotColorClasses[variant])}
        />
      )}

      {icon && <span className="mr-1.5 flex-shrink-0">{icon}</span>}

      {children}

      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          className="ml-1.5 -mr-1 p-0.5 rounded-full hover:bg-black/10 dark:hover:bg-white/10 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-offset-transparent focus:ring-current"
          aria-label="Remove"
        >
          <X size={12} strokeWidth={3} />
        </button>
      )}
    </span>
  );
};

// --- Styles ---

const sizeClasses: Record<string, string> = {
  sm: 'px-2 py-0.5 text-xs',
  md: 'px-2.5 py-0.5 text-xs', // Standard
  lg: 'px-3 py-1 text-sm',
};

const dotSizeClasses: Record<string, string> = {
  sm: 'w-1 h-1',
  md: 'w-1.5 h-1.5',
  lg: 'w-2 h-2',
};

const variantClasses: Record<string, string> = {
  primary:
    'bg-primary-100 text-primary-700 border-primary-200 dark:bg-primary-500/10 dark:text-primary-400 dark:border-primary-500/20',
  secondary:
    'bg-gray-100 text-gray-700 border-gray-200 dark:bg-zinc-800 dark:text-zinc-300 dark:border-zinc-700',
  outline: 'bg-transparent text-gray-700 border-gray-300 dark:text-zinc-300 dark:border-zinc-600',
  ghost:
    'bg-transparent text-gray-700 border-transparent hover:bg-gray-100 dark:text-zinc-300 dark:hover:bg-zinc-800',

  // Semantic
  success:
    'bg-green-100 text-green-700 border-green-200 dark:bg-green-500/10 dark:text-green-400 dark:border-green-500/20',
  warning:
    'bg-yellow-100 text-yellow-700 border-yellow-200 dark:bg-yellow-500/10 dark:text-yellow-400 dark:border-yellow-500/20',
  error:
    'bg-red-100 text-red-700 border-red-200 dark:bg-red-500/10 dark:text-red-400 dark:border-red-500/20',
  info: 'bg-cyan-100 text-cyan-700 border-cyan-200 dark:bg-cyan-500/10 dark:text-cyan-400 dark:border-cyan-500/20',
  neutral:
    'bg-gray-100 text-gray-700 border-gray-200 dark:bg-zinc-800 dark:text-zinc-400 dark:border-zinc-700',
  offline:
    'bg-zinc-100 text-zinc-700 border-zinc-200 dark:bg-zinc-800 dark:text-zinc-400 dark:border-zinc-700',
};

// Dot colors match text colors mostly
const dotColorClasses: Record<string, string> = {
  primary: 'bg-primary-600 dark:bg-primary-400',
  secondary: 'bg-gray-500 dark:bg-zinc-400',
  outline: 'bg-gray-500 dark:bg-zinc-400',
  ghost: 'bg-gray-500 dark:bg-zinc-400',

  success: 'bg-green-600 dark:bg-green-400',
  warning: 'bg-yellow-600 dark:bg-yellow-400',
  error: 'bg-red-600 dark:bg-red-400',
  info: 'bg-cyan-600 dark:bg-cyan-400',
  neutral: 'bg-gray-500 dark:bg-zinc-400',
  offline: 'bg-zinc-500 dark:bg-zinc-400',
};
